import axios from 'axios';
import { useLoginStore } from 'store/login';
import { computed, inject, ref } from 'vue';
import type { VueCookies } from 'vue-cookies';

import { type SendEvent, useEventHub } from '@/composables/eventHub';
import type { FischerPlugin, SsrCookiesObject } from '@/lib/fischerPlugin';
import { useNotificationStore } from '@/store/pinia/notification';
import { getIsIds } from '@/utils/idsTools';
import parseTokenData from '@/utils/parseTokenData';

import { useCookies } from './cookies';
import { useFischer } from './fischerPlugin';

const cookieName = 'site-login-init';
export const usernameCookie = 'login-dialog-user';
export const loginDialogCookies = 'login-dialog';
export const loggedInMessage = 'logged-in';
export const allowedStateChars =
  'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789_.-'; // NOTE: leave the minus in last place, so this can be used as a character class

export const siteLoginDebugKey = 'site-login-debug';
export const loginChannel = 'site-login';

export const createId = () => {
  const stateIdValues = new Uint32Array(16);
  // @ts-ignore
  const crypto = window.crypto || window.msCrypto;
  crypto.getRandomValues(stateIdValues);
  return Array.from(stateIdValues)
    .map((uint) => allowedStateChars.charAt(uint % allowedStateChars.length))
    .join('');
};

const base64urlToBase64 = (base64url: string) => base64url.replace(/-/g, '+').replace(/_/g, '/');

export const isNonceError = (idToken: string, originalNonce: string) => {
  try {
    const [, dataBase64] = idToken.split('.');
    const dataJson = atob(base64urlToBase64(dataBase64));
    const { nonce } = JSON.parse(dataJson);
    return nonce !== originalNonce;
  } catch (error) {
    return true;
  }
};

export type KeyedContents = Record<string, string | number | null | undefined>;

type LogDebug = (...args: any[]) => void;
type GetCookies = () => KeyedContents;

export const getAuthorizationUrl = (
  queryParams: KeyedContents = {},
  hashParams: KeyedContents = {},
  baseUrl: string = `${import.meta.env.VITE_OPENID_SERVICES}authorizationserver/oauth/authorize`,
) => {
  const merged = new URLSearchParams();
  [queryParams, hashParams].forEach((params) =>
    Object.entries(params).forEach(([key, value]) => merged.set(key, String(value))),
  );
  const separator = Object.keys(queryParams).length ? '?' : '#';
  return `${baseUrl.split('?')[0]}${separator}${merged}`;
};

export const logDebugFunction: LogDebug = (isDebug: boolean, ...params: any[]) => {
  if (isDebug) {
    console.info(...params);
  }
};

export const getCookiesFunction = (vueCookies: VueCookies, logDebug: LogDebug) => {
  const cookies = vueCookies.get(cookieName) || {};
  logDebug('get cookies', cookies?.time);
  return cookies;
};

export const setCookiesFunction = async (
  fischer: FischerPlugin,
  logDebug: LogDebug,
  vueCookies: VueCookies,
  getCookies: GetCookies,
  criticalLoginFailure: (reason: string) => void,
  cookies: KeyedContents,
) => {
  const time = Date.now();
  logDebug('setting cookies', cookieName, time);

  cookies.time = time;

  vueCookies.set(
    cookieName,
    cookies,
    '1h',
    '/',
    IS_SHOWROOM ? undefined : fischer.hostname(),
    fischer.isSecureConnection(),
    'Lax',
  );

  if (getCookies().time !== time) {
    console.info('cookie was not stored, waiting a moment');
    await new Promise((resolve) => setTimeout(resolve, 500));

    if (getCookies().time !== time) {
      criticalLoginFailure('Failed to set cookies');
      return false;
    }
  }

  logDebug('cookies set');
  return true;
};

export const handleDebugFlagFunction = (fischer: FischerPlugin): boolean => {
  if (
    fischer.query(siteLoginDebugKey) ||
    new RegExp(`(?:#|&)${siteLoginDebugKey}=true(?:&|$)`).test(window.location.hash)
  ) {
    // @ts-ignore
    window.activateLoginDebug?.(true);
    return true;
  }
  return false;
};

export const postAccessTokeninSessionFunction = async (
  logDebug: LogDebug,
  sendEvent: SendEvent,
  accessToken: string,
  idToken: string,
): Promise<string> => {
  try {
    logDebug('posting token to sitecore...');
    const result = await axios.post('/webapi/shop/userapi/Login', {
      User: {
        AccessToken: accessToken,
        IdToken: idToken,
        IsIdsSession: getIsIds(),
      },
    });
    const successPage = result.data?.LoginSuccessPage;
    logDebug('posted token to sitecore', successPage);

    // send eventHub tracking
    await sendEvent('login', {
      userId: parseTokenData(idToken).email,
    });

    return successPage;
  } catch (error) {
    throw new Error();
  }
};

export const goToLoginFunction = (
  logDebug: LogDebug,
  clientId: string,
  authorizeRedirect: string | null,
  redirectUri: string | undefined,
  state: string,
  nonce: string,
  scope: string,
  responseType: string,
) => {
  logDebug('initializing authorization');
  try {
    const params: SsrCookiesObject = {
      response_type: responseType,
      client_id: clientId,
      redirect_uri: redirectUri || String(window.location).split('#')[0],
      scope,
      state,
      nonce,
    };

    if (authorizeRedirect) {
      const searchParams = new URLSearchParams(authorizeRedirect.replace(/^.*\?/, ''));
      searchParams.forEach((key, value) => {
        if (!params[key]) {
          params[key] = value;
        }
      });
    }

    window.location.href = getAuthorizationUrl(params);
  } catch (error) {
    console.error('SSO login failed', error);
  }
};

export const setDevTokenFunction = (
  vueCookies: VueCookies,
  fischer: FischerPlugin,
  accessToken: string,
  idToken: string,
) => {
  if (!IS_SHOWROOM) {
    return;
  }
  const { exp }: { exp: number } = parseTokenData(idToken);
  const ttl = exp - Date.now() / 1000;
  vueCookies.set('IdToken', idToken, ttl, undefined, undefined, undefined, 'Strict');
  fischer.user.setAccessToken(accessToken, String(ttl));
};

export const useLogin = () => {
  const fischer = useFischer();
  const isDebug = ref<boolean>(true); // false);
  const hasCriticalLoginFailure = ref<boolean>(false);
  const { cookies: vueCookies } = useCookies();
  const { sendEvent } = useEventHub();
  const loginStore = useLoginStore();
  const notificationStore = useNotificationStore();

  const fields = inject<{
    apiKey?: {
      value?: string;
    };
  }>('fields');

  const criticalLoginFailure = (...args: any[]): void => {
    console.error(...args);
    notificationStore.openGeneralErrorNotification();
    hasCriticalLoginFailure.value = true;
  };

  const getAuthorizeRedirect = (query?: string) => {
    if (!query) {
      return null;
    }
    const withoutStateAndNonce = query
      .replace(/(\?|&)state=[^&]*/, '')
      .replace(/(\?|&)nonce=[^&]*/, '');
    return `${withoutStateAndNonce}&state=retry`;
  };

  const authorizeRedirect = computed<string | null>(() => {
    const query = fischer.query('authorize_redirect');
    return getAuthorizeRedirect(query);
  });

  const logDebug: LogDebug = (...args: any[]): void => {
    if (isDebug.value) {
      console.info(...args);
    }
  };

  const getCookies = () => getCookiesFunction(vueCookies, logDebug);

  const setCookies = async (cookies: KeyedContents) =>
    setCookiesFunction(fischer, logDebug, vueCookies, getCookies, criticalLoginFailure, cookies);

  const postAccessTokenInSession = async (
    accessToken: string,
    idToken: string,
  ): Promise<string> => {
    try {
      return await postAccessTokeninSessionFunction(logDebug, sendEvent, accessToken, idToken);
    } catch (error) {
      criticalLoginFailure('Could not post access token', error);
      throw error;
    }
  };

  const isSpecialPage = (url: string): boolean =>
    [
      fischer.pages.registration()?.url,
      fischer.pages.registrationSuccess()?.url,
      fischer.pages.logout()?.url,
      fischer.pages.logoutSuccess()?.url,
      fischer.pages.login()?.url,
      fischer.pages.loginSuccessPage()?.url,
      fischer.pages.activation()?.url,
      fischer.pages.passwordForgotten()?.url,
      fischer.pages.assistedService()?.url,
    ].includes(url);

  const getClientId = () => fischer.baseSiteId().toLowerCase();

  const goToLoginPage = (
    redirectUri: string | undefined,
    state: string,
    nonce: string,
    scope: string,
    responseType: string,
  ) =>
    goToLoginFunction(
      logDebug,
      getClientId(),
      authorizeRedirect.value,
      redirectUri,
      state,
      nonce,
      scope,
      responseType,
    );

  const clearOldStates = (cookies: Record<string, { validUntil: number }>) => {
    const maxTime = new Date().getTime();
    const keys = Object.keys(cookies);
    const allLoginKeysWithTimes: [validUntil: number, key: string][] = [];
    const maxKeys = 10; // cookies are limited to 4k in size, limit to 5 attempts (states + nonce)
    keys.forEach((key) => {
      if (/^(?:state|nonce)-/.test(key)) {
        const { validUntil } = cookies[key];
        if (!(validUntil > maxTime)) {
          console.info('Removing outdated cookie', validUntil);

          delete cookies[key];
        } else {
          allLoginKeysWithTimes.push([validUntil, key]);
        }
      }
    });
    if (allLoginKeysWithTimes.length > maxKeys) {
      console.info('Max login cookies exceeded');
      allLoginKeysWithTimes.sort((a, b) => b[0] - a[0]);

      allLoginKeysWithTimes.slice(maxKeys).forEach((item) => delete cookies[item[1]]);
    }
  };

  const createTokenState = async (formPage?: string, data?: string) => {
    const tokenStateId = createId();
    const tokenNonce = createId();

    const search = new URLSearchParams(window.location.search);
    let redirect = search.get('redirect') || formPage || fischer.uri();
    [
      'redirect',
      'brand',
      'ui_locales',
      'authorize_redirect',
      'logged-in',
      'authentication_error',
    ].forEach((param) => search.delete(param));
    const searchString = search.toString();
    if (searchString && redirect && !redirect.includes('?')) {
      redirect = `${redirect}?${searchString}`;
    }

    if (redirect && window.location.hash && window.location.hash !== '#') {
      redirect = `${redirect}${window.location.hash}`;
    }

    const cartId = fischer.cartId();
    const validUntil = new Date().getTime() + 60 * 60 * 5 * 10000;
    const state: {
      redirect: string;
      cartId?: string;
      validUntil: number;
      data?: string;
    } = { redirect, cartId, validUntil };
    if (data) {
      state.data = data;
    }
    if (!state.cartId) {
      delete state.cartId;
    }

    const cookies = getCookies();
    clearOldStates(cookies);
    cookies[`state-${tokenStateId}`] = state;
    cookies[`nonce-${tokenStateId}`] = { value: tokenNonce, validUntil };
    if (!(await setCookies(cookies))) {
      throw new Error('Could not create token state');
    }

    return { tokenStateId, tokenNonce };
  };

  const getAuthParams = async (
    customFormPage?: boolean,
    dataParam?: string,
    forceLanguage?: string | false,
  ) => {
    let lang = forceLanguage || fischer.siteLanguage();
    if (lang === 'da') {
      lang = 'da-DK';
    }
    const loginPage = fischer.pages.login().url;
    const redirectUri = loginPage
      ? `${fischer.hostUrlBase()}${loginPage}`
      : String(window.location).split('#')[0];
    logDebug('redirecting to loginFormPage');
    const clientId =
      fischer.settings()?.shop?.clientIdOverwrite || fischer.baseSiteId().toLowerCase();

    let pathname = window.location.pathname;
    if (loginStore.forceStart || isSpecialPage(pathname)) {
      pathname = `/${lang.toLowerCase()}`;
    }

    let data = dataParam;
    if (!data) {
      const brand = fischer.query('brand');
      if (brand) {
        data = /@(.*)/.exec(brand)?.[1];
      }
    }

    const brand = customFormPage
      ? `dialog:${pathname}${data ? '@' + data : ''}`
      : clientId.split('-')[0];
    const { tokenStateId: state, tokenNonce: nonce } = await createTokenState(
      customFormPage ? brand : undefined,
      data,
    );
    const siteLanguage = fischer.siteLanguage();
    const queryParams = {
      brand,
      ui_locales: siteLanguage,
      api_key: fields?.apiKey?.value || '',
    };
    const hashParams = {
      response_type: 'id_token token',
      scope: 'openid',
      redirect_uri: redirectUri,
      state,
      nonce,
      brand,
      client_id: clientId,
      ui_hint: siteLanguage,
    };
    if (isDebug.value) {
      // @ts-ignore
      hashParams[siteLoginDebugKey] = true;
    }

    return { queryParams, hashParams, brand };
  };

  const initializeAuthorization = async (
    customFormPage?: boolean,
    data?: string,
    forceLanguage?: string | false,
  ) => {
    if (fischer.app.isEditMode()) {
      console.debug('not logging in because of edit mode');
      return;
    }
    const { queryParams, hashParams, brand } = await getAuthParams(
      customFormPage,
      data,
      forceLanguage,
    );
    const newLocation = getAuthorizationUrl(queryParams, hashParams);
    logDebug('new location', newLocation);
    if (customFormPage) {
      logDebug('setting login-dialog', brand);
      vueCookies.set(loginDialogCookies, brand);
    }
    if (fischer.query('debug-no-redirect')) {
      console.info('debug-no-redirect');
      return;
    }
    window.location.href = newLocation;
  };

  const setDevToken = (accessToken: string, idToken: string) =>
    setDevTokenFunction(vueCookies, fischer, accessToken, idToken);

  const watchTabLogins = (callback: () => void): BroadcastChannel | null => {
    try {
      const bc = new BroadcastChannel(loginChannel);
      bc.onmessage = (event) => {
        const [type] = event?.data || [];
        if (type === loggedInMessage) {
          callback();
        } else {
          console.error('unknown login bc event');
        }
      };
      logDebug('created login bc');
      return bc;
    } catch (error) {
      console.error('Could not create login bc', error);
      return null;
    }
  };

  const notifyLogin = () => {
    try {
      const bc = new BroadcastChannel(loginChannel);
      bc?.postMessage([loggedInMessage]);
      logDebug('notified login');
    } catch (error) {
      console.error('Could not notify login', error);
    }
  };

  return {
    isDebug,
    hasCriticalLoginFailure,
    logDebug,
    postAccessTokenInSession,
    initializeAuthorization,
    isSpecialPage,
    authorizeRedirect,
    getAuthorizeRedirect,
    getCookies,
    setCookies,
    setDevToken,
    criticalLoginFailure,
    goToLoginPage,
    watchTabLogins,
    notifyLogin,
    getAuthParams,
    getAuthorizationUrl,
  };
};
